import {UniversalLoginInstance} from 'app/modules/auth/types/universalLogin'
import clsx from 'clsx'
import {
  getPublicLoginPath,
  isAuthGradientDomain,
  isSelectiveAiDomains,
} from 'common/HelperFunctions'
import Spinner from 'common/components/Spinner'
import {useFormik} from 'formik'
import {useCallback, useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import {Link, useLocation, useNavigate} from 'react-router'
import {toast} from 'react-toastify'
import {
  requestPasswordResetOtp,
  verifyPasswordResetGoogle,
  verifyPasswordResetOtp,
} from 'setup/redux/Actions/SignIn/forgotPassword'
import * as Yup from 'yup'

import {systemMode, useThemeMode} from '_metronic/partials'

import {UniversalLoginSchoolPicker} from '../modals/UniversalLoginSchoolPicker'
import {ForgotSetPasswordGoogleButton} from './ForgotSetPasswordGoogleButton'
import {ForgotSetPasswordOtp} from './ForgotSetPasswordOtp'
import {
  clearPendingGooglePasswordReset,
  readPendingGooglePasswordReset,
} from './forgotSetPasswordStorage'
import {isPasswordResetConflict} from './passwordResetConflict'
import {
  getPasswordResetRequestOtpError,
  getPasswordResetVerifyError,
  handleVerifyPasswordResetSuccess,
} from './passwordResetHelpers'
import {
  EmailPurpose,
  ForgotSetPasswordStep,
  PasswordResetPendingAction,
  RequestOtpResult,
  VerifyPasswordResetResult,
} from './types'

const emailSchema = Yup.object().shape({
  email: Yup.string().email().min(3).max(50).required().label('Email'),
})

export function ForgotSetPasswordFlow() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const isSpecialDomain = isAuthGradientDomain() || isSelectiveAiDomains()
  // Gradient domains always paint a dark page background; other selective-AI domains
  // follow the theme. White text is only readable when the background is actually dark.
  const {mode} = useThemeMode()
  const onDarkBg = isAuthGradientDomain() || (mode === 'system' ? systemMode : mode) === 'dark'

  const [step, setStep] = useState<ForgotSetPasswordStep>('email')
  const [emailPurpose, setEmailPurpose] = useState<EmailPurpose | null>(null)
  const [applicationId, setApplicationId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [showSchoolPicker, setShowSchoolPicker] = useState(false)
  const [schoolInstances, setSchoolInstances] = useState<UniversalLoginInstance[]>([])
  const [pickerLoading, setPickerLoading] = useState(false)
  const [pendingAction, setPendingAction] = useState<PasswordResetPendingAction | null>(null)
  const [pendingOtp, setPendingOtp] = useState<string | null>(null)
  const [pendingIdToken, setPendingIdToken] = useState<string | null>(null)

  const formik = useFormik({
    initialValues: {email: (location.state as {email?: string})?.email ?? ''},
    validationSchema: emailSchema,
    onSubmit: () => {
      handleRequestOtp()
    },
  })

  const handlePasswordFormSuccess = useCallback(
    (res: {result?: VerifyPasswordResetResult}) => {
      if (!handleVerifyPasswordResetSuccess(navigate, res?.result)) {
        toast.error('Could not start password reset session')
      }
    },
    [navigate]
  )

  const handleConflict = useCallback(
    (res: unknown, action: PasswordResetPendingAction, otp?: string, idToken?: string) => {
      if (!isPasswordResetConflict(res as Parameters<typeof isPasswordResetConflict>[0])) {
        return false
      }
      const conflict = res as {result: {instances: UniversalLoginInstance[]}}
      setSchoolInstances(conflict.result.instances)
      setPendingAction(action)
      setPendingOtp(otp ?? null)
      setPendingIdToken(idToken ?? null)
      setShowSchoolPicker(true)
      setLoading(false)
      setResendLoading(false)
      setPickerLoading(false)
      return true
    },
    []
  )

  const handleRequestOtp = useCallback(
    (appId: number | null = applicationId, isResend = false) => {
      if (isResend) {
        setResendLoading(true)
      } else {
        setLoading(true)
      }
      dispatch(
        requestPasswordResetOtp(
          formik.values.email.trim(),
          appId,
          (res: {result?: RequestOtpResult; message?: string}) => {
            setLoading(false)
            setResendLoading(false)
            const purpose = res?.result?.emailPurpose
            if (purpose === 'set' || purpose === 'reset') {
              setEmailPurpose(purpose)
            }
            if (!isResend) {
              setStep('otp')
            }
            toast.success(res?.message ?? 'If an account exists, we sent a verification code.')
          },
          (err: unknown) => {
            if (handleConflict(err, 'forgot')) return
            setLoading(false)
            setResendLoading(false)
            toast.error(
              getPasswordResetRequestOtpError(
                err as {statusCode?: number; message?: string},
                formik.values.email.trim()
              )
            )
          }
        )
      )
    },
    [applicationId, dispatch, formik.values.email, handleConflict]
  )

  const handleVerifyOtp = useCallback(
    (otp: string, appId: number | null = applicationId) => {
      setLoading(true)
      dispatch(
        verifyPasswordResetOtp(
          formik.values.email.trim(),
          otp,
          appId,
          (res: {result?: VerifyPasswordResetResult}) => {
            setLoading(false)
            handlePasswordFormSuccess(res)
          },
          (err: unknown) => {
            if (handleConflict(err, 'verify_otp', otp)) return
            setLoading(false)
            toast.error(
              getPasswordResetVerifyError(err as {statusCode?: number; message?: string}, 'otp')
            )
          }
        )
      )
    },
    [applicationId, dispatch, formik.values.email, handleConflict, handlePasswordFormSuccess]
  )

  const handleVerifyGoogle = useCallback(
    (idToken: string, appId: number | null = applicationId) => {
      setLoading(true)
      const typedEmail = formik.values.email.trim() || undefined
      dispatch(
        verifyPasswordResetGoogle(
          idToken,
          appId,
          (res: {result?: VerifyPasswordResetResult}) => {
            setLoading(false)
            clearPendingGooglePasswordReset()
            handlePasswordFormSuccess(res)
          },
          (err: unknown) => {
            if (handleConflict(err, 'verify_google', undefined, idToken)) return
            setLoading(false)
            clearPendingGooglePasswordReset()
            toast.error(
              getPasswordResetVerifyError(err as {statusCode?: number; message?: string}, 'google')
            )
          },
          typedEmail
        )
      )
    },
    [applicationId, dispatch, formik.values.email, handleConflict, handlePasswordFormSuccess]
  )

  const retryPendingAction = useCallback(
    (appId: number) => {
      setApplicationId(appId)
      setShowSchoolPicker(false)
      setPickerLoading(true)

      if (pendingAction === 'forgot') {
        handleRequestOtp(appId)
      } else if (pendingAction === 'verify_otp' && pendingOtp) {
        handleVerifyOtp(pendingOtp, appId)
      } else if (pendingAction === 'verify_google' && pendingIdToken) {
        handleVerifyGoogle(pendingIdToken, appId)
      } else {
        setPickerLoading(false)
      }
    },
    [
      handleRequestOtp,
      handleVerifyGoogle,
      handleVerifyOtp,
      pendingAction,
      pendingIdToken,
      pendingOtp,
    ]
  )

  useEffect(() => {
    const state = location.state as {
      googleIdToken?: string
      conflictInstances?: UniversalLoginInstance[]
      email?: string
    } | null

    if (state?.email) {
      formik.setFieldValue('email', state.email)
    }

    if (state?.conflictInstances?.length && state.googleIdToken) {
      setSchoolInstances(state.conflictInstances)
      setPendingIdToken(state.googleIdToken)
      setPendingAction('verify_google')
      setShowSchoolPicker(true)
      return
    }

    const pending = readPendingGooglePasswordReset()
    if (pending.idToken) {
      handleVerifyGoogle(pending.idToken)
    }
    // Run once on mount for Google return handoff
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const titleClass = isSpecialDomain && onDarkBg ? 'text-white' : 'text-dark'
  const subtitleClass = isSpecialDomain && onDarkBg ? 'text-white-50' : 'text-gray-500'

  if (showSchoolPicker) {
    return (
      <UniversalLoginSchoolPicker
        show={showSchoolPicker}
        instances={schoolInstances}
        loading={pickerLoading}
        onSelect={(instance) => retryPendingAction(instance.applicationId)}
        onClose={() => {
          if (pickerLoading) return
          setShowSchoolPicker(false)
          setSchoolInstances([])
          setPendingAction(null)
        }}
      />
    )
  }

  if (step === 'otp') {
    return (
      <div>
        <div className='text-center mb-10'>
          <h1 className={clsx('fw-bolder mb-3', titleClass)}>Verify Your Email</h1>
          <div className={clsx('fw-semibold fs-6', subtitleClass)}>
            {emailPurpose === 'set'
              ? 'Confirm your email to set your password.'
              : emailPurpose === 'reset'
                ? 'Confirm your email to reset your password.'
                : 'Enter the verification code we sent to your email.'}
          </div>
        </div>
        <ForgotSetPasswordOtp
          email={formik.values.email}
          verifyLoading={loading}
          resendLoading={resendLoading}
          onDarkBg={isSpecialDomain && onDarkBg}
          onSubmit={handleVerifyOtp}
          onResend={() => handleRequestOtp(applicationId, true)}
          onBack={() => setStep('email')}
        />
      </div>
    )
  }

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        <h1 className={clsx('fw-bolder mb-3', titleClass)}>Forgot / Set Password?</h1>
        <div className={clsx('fw-semibold fs-6', subtitleClass)}>
          Enter your email for a verification code, or continue with Google to set or reset your
          password.
        </div>
      </div>

      <div className='fv-row mb-8'>
        <label className={clsx('form-label fw-bolder fs-6', titleClass)}>Email</label>
        <input
          type='email'
          autoComplete='email'
          {...formik.getFieldProps('email')}
          className={clsx('form-control form-control-lg', {
            'is-invalid': formik.touched.email && formik.errors.email,
            'form-control-solid': !(isSpecialDomain && onDarkBg),
          })}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>

      <div className='text-center mb-5'>
        <button
          type='submit'
          className='btn btn-lg btn-primary fw-bolder w-100 rounded'
          disabled={loading || !formik.isValid}
        >
          {loading ? <Spinner size='sm' /> : <span>Send Verification Code</span>}
        </button>
      </div>

      <ForgotSetPasswordGoogleButton
        disabled={loading}
        isSpecialDomain={isSpecialDomain && onDarkBg}
      />

      <div className='text-center mt-6'>
        <Link to={getPublicLoginPath()} className='btn btn-light w-100 rounded'>
          Cancel
        </Link>
      </div>
    </form>
  )
}
